import { Trans, useLingui } from '@lingui/react/macro';
import { Command as Cmdk } from 'cmdk';
import { ArrowUpRight, CornerDownLeft, Search, Star } from 'lucide-react';
import { useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';

import { cn } from '@/shared/ui/cn';

import { shortcutLabel } from './hotkey';
import type { Command, CommandGroup } from './model';

const GROUPS: readonly CommandGroup[] = ['goto', 'screen', 'preferences'];

const valueOf = (command: Command) => `${command.label}\u0000${command.hint ?? ''}\u0000${command.id}`;

/** ⌘D (Ctrl+D fora do Mac): fixa ou desafixa na Início a tela do item selecionado. */
const isPinKey = (event: KeyboardEvent) => event.key.toLowerCase() === 'd' && (event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey;

const fold = (text: string) => text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

/** Busca por palavra, sem acento: "cess" acha Cessionários antes de qualquer casamento solto. */
export function scoreCommand(value: string, search: string, keywords: readonly string[] = []): number {
  const q = fold(search).trim();
  if (!q) return 1;
  const [label = '', hint = ''] = value.split('\u0000').map(fold);
  const terms = q.split(/\s+/);
  let score = 0;
  for (const term of terms) {
    if (label.startsWith(term)) score += 1;
    else if (label.split(/[\s/.-]+/).some((w) => w.startsWith(term))) score += 0.8;
    else if (label.includes(term)) score += 0.6;
    else if (hint.split(/\s+/).some((w) => w.startsWith(term))) score += 0.3;
    else if (keywords.some((k) => fold(k).includes(term))) score += 0.2;
    else return 0;
  }
  return score / terms.length;
}

/** `renderIcon` fica com quem chama: `shared` não conhece o mapa de ícones da casca. */
export function CommandPalette(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  commands: readonly Command[];
  renderIcon?: (name: string) => ReactNode;
}) {
  const { t } = useLingui();
  const [selected, setSelected] = useState('');
  const titles: Record<CommandGroup, string> = { goto: t`Ir para`, screen: t`Nesta tela`, preferences: t`Preferências` };
  const run = (command: Command) => {
    props.onOpenChange(false);
    void command.run();
  };
  const current = props.commands.find((command) => valueOf(command) === selected);
  const onKeyDown = (event: KeyboardEvent) => {
    if (!isPinKey(event)) return;
    event.preventDefault();
    current?.pin?.toggle();
  };
  // O toque na estrela fixa sem abrir a tela; no teclado, o mesmo vale pelo ⌘D (a estrela fica fora da árvore acessível).
  const togglePin = (command: Command) => (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    command.pin?.toggle();
  };
  return (
    <Cmdk.Dialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      label={t`Paleta de comandos`}
      value={selected}
      onValueChange={setSelected}
      onKeyDown={onKeyDown}
      filter={scoreCommand}
      overlayClassName="fixed inset-0 z-50 bg-scrim backdrop-blur-[2px] data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out"
      contentClassName="fixed inset-0 z-50 flex flex-col overflow-hidden bg-card md:inset-auto md:left-1/2 md:top-[12vh] md:w-[640px] md:-translate-x-1/2 md:rounded-xl md:border md:border-border md:shadow-3 data-[state=open]:animate-fade-in data-[state=closed]:animate-fade-out"
    >
      <div className="flex items-center gap-2 border-b border-border px-4 pt-[env(safe-area-inset-top,0px)] md:pt-0">
        <Search className="size-5 text-muted-foreground" aria-hidden />
        <Cmdk.Input
          placeholder={t`Digite para buscar telas e ações`}
          className="h-14 flex-1 bg-transparent text-base outline-none placeholder:text-faint md:h-12 md:text-sm"
        />
        <button type="button" onClick={() => props.onOpenChange(false)} className="text-sm font-medium text-accent-text md:hidden">
          <Trans>Cancelar</Trans>
        </button>
      </div>
      <Cmdk.List className="max-h-none flex-1 overflow-y-auto p-2 md:max-h-[420px]">
        <Cmdk.Empty className="px-3 py-8 text-center text-sm text-muted-foreground">
          <Trans>Nada encontrado.</Trans>
        </Cmdk.Empty>
        {GROUPS.map((group) => {
          const items = props.commands.filter((c) => c.group === group);
          if (items.length === 0) return null;
          return (
            <Cmdk.Group
              key={group}
              heading={titles[group]}
              className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wide [&_[cmdk-group-heading]]:text-muted-foreground"
            >
              {items.map((command) => (
                <Cmdk.Item
                  key={command.id}
                  value={valueOf(command)}
                  keywords={[...command.keywords]}
                  onSelect={() => run(command)}
                  className="group/cmd flex min-h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-base transition-colors duration-100 data-[selected=true]:bg-accent-wash data-[selected=true]:text-accent-text md:min-h-10 md:text-sm"
                >
                  {props.renderIcon ? (
                    <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground group-data-[selected=true]/cmd:bg-card group-data-[selected=true]/cmd:text-accent">
                      {command.icon ? props.renderIcon(command.icon) : null}
                    </span>
                  ) : null}
                  <span className="flex-1 truncate">
                    {command.label}
                    {command.pin?.pinned ? <span className="sr-only">{t`, fixada na Início`}</span> : null}
                  </span>
                  {command.hint ? <span className="truncate text-xs text-muted-foreground">{command.hint}</span> : null}
                  {command.pin ? (
                    <span
                      aria-hidden
                      data-slot="command-pin"
                      data-pinned={command.pin.pinned}
                      title={command.pin.pinned ? t`Remover da Início` : t`Fixar na Início`}
                      onClick={togglePin(command)}
                      className={cn(
                        'inline-flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors duration-100 hover:bg-card hover:text-accent-text md:size-7',
                        command.pin.pinned ? 'text-accent-text' : 'opacity-0 group-hover/cmd:opacity-100 group-data-[selected=true]/cmd:opacity-100 max-md:opacity-100',
                      )}
                    >
                      <Star className={cn('size-4', command.pin.pinned && 'fill-current')} />
                    </span>
                  ) : null}
                  {command.external ? <ArrowUpRight className="size-3.5 shrink-0 text-muted-foreground" aria-hidden /> : null}
                  <CornerDownLeft className="hidden size-3.5 shrink-0 text-muted-foreground group-data-[selected=true]/cmd:md:block" aria-hidden />
                  {command.shortcut ? <kbd className="hidden rounded border border-input px-1.5 font-mono text-[11px] text-muted-foreground md:inline">{command.shortcut}</kbd> : null}
                </Cmdk.Item>
              ))}
            </Cmdk.Group>
          );
        })}
      </Cmdk.List>
      {current?.pin ? (
        <p data-slot="command-pin-hint" className="hidden items-center gap-1.5 border-t border-border px-4 py-2 text-xs text-muted-foreground md:flex">
          <kbd className="rounded border border-input px-1.5 font-mono text-[11px]">{shortcutLabel('d')}</kbd>
          {current.pin.pinned ? t`remove esta tela da Início` : t`fixa esta tela na Início`}
        </p>
      ) : null}
    </Cmdk.Dialog>
  );
}
